import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit, TemplateRef } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { iif, Observable, of, Subject } from 'rxjs';
import { map, mergeMap, takeUntil } from 'rxjs/operators';

import { UrlService } from '@services/url/url.service';
import { AffiliateService } from '@shared/services/affiliate/affiliate.service';
import { ChatDeskService } from '@shared/services/chat-desk/chat-desk.service';
import { LocalStorageService } from '@shared/services/local-storage/local-storage.service';
import { selectAffiliateOptionsData } from '@store/affiliate-options/affiliate-options.selectors';
import { SessionState } from 'src/app/core/store/session/session.reducer';
import { selectSessionData } from 'src/app/core/store/session/session.selectors';
import { AppState } from 'src/app/core/store/state';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent implements OnInit, OnDestroy {
  modalRef: BsModalRef;
  session: SessionState;
  hasAffiliates$: Observable<boolean>;
  private unsubscribeAll$: Subject<void> = new Subject();

  constructor(
    @Inject(BsModalService) private _modalService: BsModalService,
    @Inject(AffiliateService) private _affiliateService: AffiliateService,
    @Inject(Store) private _store: Store<AppState>,
    @Inject(Router) private _router: Router,
    @Inject(UrlService) private _urlService: UrlService,
    @Inject(LocalStorageService) private _localStorageService: LocalStorageService,
    @Inject(ChatDeskService) private chatDeskService: ChatDeskService
  ) {}

  ngOnInit(): void {
    this.hasAffiliates$ = this.initAffiliates();
    this._store
      .select(selectSessionData)
      .pipe(takeUntil(this.unsubscribeAll$))
      .subscribe((data: SessionState) => (this.session = data));
  }

  openModal(template: TemplateRef<any>, classNames?: string): void {
    this.modalRef = this._modalService.show(template, { class: `modal-dialog-centered ${classNames}` });
  }

  logOut(): void {
    const logoutURLMyFF: string = this._urlService.getLogoutUrl();
    localStorage.clear();
    sessionStorage.clear();
    window.open(logoutURLMyFF, '_self');
  }

  initAffiliates(): Observable<boolean> {
    return this._store.select(selectAffiliateOptionsData).pipe(
      mergeMap((affiliates) => iif(() => !affiliates, this._affiliateService.getAffiliateOptions(), of(affiliates))),
      map((affiliates) => affiliates && affiliates.length > 0)
    );
  }

  navigateToOverview(): void {
    this._router.navigate(['/overview']);
  }

  navigateToFareFinderPage(): void {
    let fareFinderUrl: string = '';
    this._urlService.getFarefinderRedirectUrl().subscribe((url) => (fareFinderUrl = url));
    window.open(fareFinderUrl, '_blank');
  }

  ngOnDestroy(): void {
    this.unsubscribeAll$.next();
    this.unsubscribeAll$.complete();
  }

  get selectedAffiliateCode(): string | null {
    if (this.session?.selectedAffiliate?.code) {
      return this.session?.selectedAffiliate?.code;
    } else {
      return this._localStorageService.getSelectedAffiliate()?.Code;
    }
  }
}
